import React, {FC, useState, useEffect} from 'react'
import {Form, Formik} from 'formik'
import {ToastContainer} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import {updateCustomer, createCustomer} from './Api'
import {
  corporateTabs,
  corporateToCustomerPayload,
  ICorporateModel,
  initialCorporate,
  validationCorporate,
} from './CorporateModel'
import {
  CUSTOMER_CLASS,
  CUSTOMER_TYPES,
  HGS_TYPES,
  INVOICE_TYPES,
  LANGUAGE,
  LICENSE_TYPES,
  COUNTRY_OPTIONS,
  RISK_TYPES,
  STOPPAGE_CODES,
  STOPPAGE_TYPES,
  YES_OR_NO,
} from '../../../shared/models/SharedConstantLookups'
import SelectInput from '../../../shared/components/SelectInput'
import {showErrorToast, showSuccessToast} from '../../../shared/components/ToastUtils'
import FormTabHeader from '../../../shared/components/FormTabHeader'
import TextInput from '../../../shared/components/TextInput'
import PhoneInput from '../../../shared/components/PhoneInput'
import CheckboxInput from '../../../shared/components/CheckboxInput'
import TextAreaInput from '../../../shared/components/TextAreaInput'
import {branches, currencies, personels, tariffs} from '../../../shared/models/SharedApiLookups'
import { Button } from 'react-bootstrap-v5'
import { AsyncReservationSourceModel } from '../../../shared/models/ApiModels'
import AsyncSearchInput from '../../../shared/components/AsyncSearchInput'
import { fetchAsyncReservationSourceData } from '../../../shared/models/SharedApiLookups' 

export const CorporatePage: FC = () => {
  const [tab, setTab] = useState<string>('company')
  const [loading, setLoading] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null | undefined>(null)
  const [branchOptions, setBranchOptions] = useState<{value: string; label: string}[]>([])
  const [tariffOptions, setTariffOptions] = useState<{value: string; label: string}[]>([])
  const [personelOptions, setPersonelOptions] = useState<{value: string; label: string}[]>([])
  const [currencyOptions, setCurrencyOptions] = useState<{value: string; label: string}[]>([])

  useEffect(() => {
    Promise.all([branches(), tariffs(), personels(), currencies()]).then(([brn, tr, pr, cr]) => {
      setBranchOptions(brn)
      setTariffOptions(tr)
      setPersonelOptions(pr)
      setCurrencyOptions(cr)
    })
  }, [])

  const handleSave = async (values: ICorporateModel, resetForm: () => void) => {
    setLoading(true)
    try {
      const payload = corporateToCustomerPayload(values)
      if (selectedId) {
        await updateCustomer({...payload, id: selectedId, customerType: 'CORPORATE'})
        showSuccessToast('Müşteri başarıyla güncellendi')
      } else {
        await createCustomer({...payload, customerType: 'CORPORATE'})
        showSuccessToast('Müşteri başarıyla oluşturuldu')
      }
      resetForm()
      setSelectedId(null)
    } catch (err) {
      showErrorToast('Bir hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Formik
        initialValues={initialCorporate}
        validationSchema={validationCorporate}
        enableReinitialize
        onSubmit={(values, {resetForm}) => handleSave(values, resetForm)}
      >
        {(formik) => {
          const handleReset = () => {
            formik.resetForm()
            setSelectedId(null)
          }
          return (
            <>
              <FormTabHeader
                formId='corporateForm'
                formikSubmit={formik.handleSubmit}
                formikReset={handleReset}
                loading={loading}
                tab={tab}
                setTab={setTab}
                setSelectedId={setSelectedId}
                tabs={corporateTabs}
                onExportExcel={() => console.log('Excel export')}
              />
              <div className='card mb-5'>
                <Form id='corporateForm' className='form card-body'>
                  {/* ── Tab 1: Firma Bilgileri ── */}
                  {tab === 'company' && (
                    <div className='row'>
                      <div className='col-md-6'>
                        <div className='row g-4'>
                          <TextInput
                            labelPosition='top'
                            name='firstName'
                            label='Firma Adı'
                            wrapperClassName='col-12'
                          />
                          <TextInput
                            labelPosition='top'
                            name='lastName'
                            label='Devamı'
                            wrapperClassName='col-12'
                          />
                          <TextInput
                            labelPosition='top'
                            name='taxOffice'
                            label='Vergi Dairesi'
                            wrapperClassName='col-6'
                          />
                          <TextInput
                            labelPosition='top'
                            name='taxNumber'
                            type='number'
                            label='Vergi Numarası'
                            wrapperClassName='col-6'
                          />
                          <TextInput
                            labelPosition='top'
                            max={11}
                            type='number'
                            name='tcKimlikNo'
                            label='TC Kimlik No'
                            wrapperClassName='col-12'
                          />
                          <SelectInput
                            labelPosition='top'
                            name='country'
                            label='Ülke'
                            wrapperClassName='col-12'
                            options={COUNTRY_OPTIONS}
                          />
                          <TextInput
                            labelPosition='top'
                            name='city'
                            label='Şehir'
                            wrapperClassName='col-6'
                          />
                          <TextInput
                            labelPosition='top'
                            name='district'
                            label='İlçe'
                            wrapperClassName='col-6'
                          />
                          <PhoneInput name='gsm1' label='Gsm' wrapperClassName='col-6' />
                          <PhoneInput name='gsm2' label='Gsm2' wrapperClassName='col-6' />
                          <PhoneInput name='phone' label='Telefon' wrapperClassName='col-6' />
                          <TextInput
                            labelPosition='top'
                            name='emailAddress'
                            label='Mail Adresi'
                            wrapperClassName='col-6'
                          />
                          
                          <TextAreaInput
                            labelPosition='top'
                            name='address'
                            label='Adres'
                            rows={3}
                            wrapperClassName='col-12'
                          />
                          
                          <TextAreaInput
                            labelPosition='top'
                            name='description'
                            label='Açıklama'
                            rows={3}
                            wrapperClassName='col-12'
                          />
                        </div>
                      </div>

                      <div className='col-md-6'>
                        <div className='row g-4'>
                          <PhoneInput 
                            name='firstOfficerPhone'
                            label='Yetkili Kişi Telefonu'
                            wrapperClassName='col-6'
                          />
                          <TextInput
                            labelPosition='top'
                            name='firstOfficerEmail'
                            label='Yetkili Kişi Mail Adresi'
                            wrapperClassName='col-6'
                          />
                          <PhoneInput 
                            name='secondOfficerPhone'
                            label='2. Yetkili Kişi Telefonu'
                            wrapperClassName='col-6'
                          />
                          <TextInput
                            labelPosition='top'
                            name='secondOfficerEmail'
                            label='2. Yetkili Kişi Mail Adresi'
                            wrapperClassName='col-6'
                          />
                          <PhoneInput 
                            name='thirdOfficerPhone'
                            label='3. Yetkili Kişi Telefonu'
                            wrapperClassName='col-6'
                          />
                          <TextInput
                            labelPosition='top'
                            name='thirdOfficerEmail'
                            label='3. Yetkili Kişi Mail Adresi'
                            wrapperClassName='col-6'
                          />
                          
                          <TextInput
                            labelPosition='top'
                            name='corporateNumber'
                            label='Kurumsal No'
                            wrapperClassName='col-5'
                          />
                          <TextInput
                            labelPosition='top'
                            name='corporatePassword'
                            label='Kurumsal Şifre'
                            wrapperClassName='col-5'
                          />
                          <Button
                            type='button'
                            onClick={() => {
                              const password = Math.random().toString(36).substring(2, 15)
                              const corporateNumber = Math.random().toString(36).substring(2, 15)
                                formik.setFieldValue('corporatePassword', password)
                                formik.setFieldValue('corporateNumber', corporateNumber)
                            }}
                            className='btn btn-primary col-2'
                          >
                            Kod Üret
                          </Button>
                          <TextInput
                            labelPosition='top'
                            name='specialCode'
                            label='Özel Kod'
                            wrapperClassName='col-6'
                          />
                          <TextInput
                            labelPosition='top'
                            name='integrationCode'
                            label='Entegrasyon Kodu'
                            wrapperClassName='col-6'
                          />
                          <TextInput
                            labelPosition='top'
                            name='webDiscount'
                            type='number'
                            label='Web İndirim'
                            wrapperClassName='col-6'
                          />
                          <TextInput
                            labelPosition='top'
                            name='warningTime'
                            type='datetime-local'
                            label='Uyarı Liste Zamanı'
                            wrapperClassName='col-6'
                          />
                          <SelectInput
                            labelPosition='top'
                            name='operationBranch'
                            label='İşlem Şube'
                            wrapperClassName='col-6'
                            options={branchOptions}
                          />
                          <SelectInput
                            labelPosition='top'
                            name='balanceVisibleBranch'
                            label='Bakiye Görebilir'
                            wrapperClassName='col-6'
                            options={branchOptions}
                          />
                          <SelectInput
                            labelPosition='top'
                            name='customerRepresentative'
                            label='Müşteri Temsilcisi'
                            wrapperClassName='col-12'
                            options={personelOptions}
                          />
                          <TextAreaInput
                            labelPosition='top'
                            name='bankInformation'
                            label='Banka Bilgileri'
                            rows={3}
                            wrapperClassName='col-12'
                          />
                          <TextAreaInput
                            labelPosition='top'
                            name='warningMessage'
                            label='Uyarı'
                            rows={3}
                            wrapperClassName='col-12'
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ── Tab 2: Sözleşme & Detay ── */}
                  {tab === 'detail' && (
                    <div className='row'>
                      <div className='col-md-6'>
                        <div className='row g-4'>
                          <SelectInput
                            labelPosition='top'
                            name='tariff'
                            label='Tarife'
                            wrapperClassName='col-12'
                            options={tariffOptions}
                          />
                          <TextInput
                            labelPosition='top'
                            name='maturityDay'
                            label='Vade Gün'
                            wrapperClassName='col-12'
                          />
                          <SelectInput
                            labelPosition='top'
                            name='invoiceAddressDifferent'
                            label='Fatura Adresi Farklı'
                            wrapperClassName='col-12'
                            options={YES_OR_NO}
                          />
                          <SelectInput
                            labelPosition='top'
                            name='hgsResponseType'
                            label='HGS Yansıtma Türü'
                            wrapperClassName='col-12'
                            options={HGS_TYPES}
                          />
                          <SelectInput
                            labelPosition='top'
                            name='licenseType'
                            label='Ehliyet Türü'
                            wrapperClassName='col-12'
                            options={LICENSE_TYPES}
                          />
                          <SelectInput
                            labelPosition='top'
                            name='licenseCountry'
                            label='Ehliyet Ülke'
                            wrapperClassName='col-12'
                            options={COUNTRY_OPTIONS}
                          />
                          <TextInput
                            labelPosition='top'
                            name='riskMessage'
                            label='Risk Mesajı'
                            wrapperClassName='col-12'
                          />
                          <TextInput
                            labelPosition='top'
                            name='riskMessageDate'
                            type='date'
                            label='Risk Mesajı Tarihi'
                            wrapperClassName='col-12'
                          />
                          <SelectInput
                            labelPosition='top'
                            name='riskPermit'
                            label='Risk İzinli'
                            wrapperClassName='col-12'
                            options={RISK_TYPES}
                          />
                          <TextInput
                            labelPosition='top'
                            name='dealerCommissionRate'
                            type='number'
                            label='Bayi Komisyon Oranı'
                            wrapperClassName='col-12'
                          />
                        </div>
                      </div>

                      <div className='col-md-6'>
                        <div className='row g-4'>
                        <AsyncSearchInput<AsyncReservationSourceModel>
                          name='reservationSource'
                          label='Rezervasyon Kaynağı'
                          placeholder='Rezervasyon kaynağı adı girin...'
                          fetchHandler={fetchAsyncReservationSourceData}
                          displayField={(item) => `${item.reservationSourceName}`}
                          onSelect={(item) => formik.setFieldValue('reservationSource', item.id)}
                          onClear={() => formik.setFieldValue('reservationSource', '')}
                          wrapperClassName='col-md-12'
                        />
                          <SelectInput
                            labelPosition='top'
                            name='communicationLanguage'
                            label='Konuşma Dili'
                            wrapperClassName='col-12'
                            options={LANGUAGE}
                          />
                          <SelectInput
                            labelPosition='top'
                            name='currency'
                            label='Döviz'
                            wrapperClassName='col-12'
                            options={currencyOptions}
                          />
                          <SelectInput
                            labelPosition='top'
                            name='invoicePeriod'
                            label='Fatura Dönemi'
                            wrapperClassName='col-12'
                            options={INVOICE_TYPES}
                          />
                          <SelectInput
                            labelPosition='top'
                            name='customerClass'
                            label='Müşteri Sınıfı'
                            wrapperClassName='col-12'
                            options={CUSTOMER_CLASS}
                          />
                          <SelectInput
                            labelPosition='top'
                            name='customerType'
                            label='Müşteri Türü'
                            wrapperClassName='col-12'
                            options={CUSTOMER_TYPES}
                          />
                          <SelectInput
                            labelPosition='top'
                            name='stoppageType'
                            label='Tevkifat Fatura Durumu'
                            wrapperClassName='col-12'
                            options={STOPPAGE_TYPES}
                          />
                          <SelectInput
                            labelPosition='top'
                            name='stoppageCode'
                            label='Tevkifat Kodu'
                            wrapperClassName='col-12'
                            options={STOPPAGE_CODES}
                          />
                          <TextInput
                            labelPosition='top'
                            name='riskLimit'
                            currency={true}
                            label='Risk Limiti'
                            wrapperClassName='col-12'
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ── Tab 3: İzinler & Ayarlar ── */}
                  {tab === 'permission' && (
                    <div className='row'>
                      <div className='col-md-6'>
                        <div className='row g-4'>
                          <CheckboxInput
                            name='passive'
                            label='Pasif Et'
                            wrapperClassName='col-12'
                          />
                          <CheckboxInput
                            name='hideInvoiceDetail'
                            label='Fatura Detay Belirtme'
                            wrapperClassName='col-12'
                          />
                          <div className='col-12'>
                            <div className='d-flex gap-5'>
                              <CheckboxInput name='smsPermission' label='SMS İzin' />
                              <CheckboxInput name='mailPermission' label='Mail İzin' />
                              <CheckboxInput name='phonePermission' label='Telefon İzin' />
                            </div>
                          </div>
                          <CheckboxInput
                            name='birthdayTracking'
                            label='Doğum Günü Takip'
                            wrapperClassName='col-12'
                          />
                          <CheckboxInput
                            name='customerCardAnonymized'
                            label='Müşteri Kartı Anonimleştir'
                            wrapperClassName='col-12'
                          />
                          <CheckboxInput
                            name='rentalCardAnonymized'
                            label='Kira Kartı Anonimleştir'
                            wrapperClassName='col-12'
                          />
                          <CheckboxInput
                            name='reservationCardAnonymized'
                            label='Rezervasyon Kartı Anonimleştir'
                            wrapperClassName='col-12'
                          />
                          <CheckboxInput
                            name='invoiceCardAnonymized'
                            label='Fatura Kartı Anonimleştir'
                            wrapperClassName='col-12'
                          />
                          <CheckboxInput
                            name='cashboxCardAnonymized'
                            label='Banka-Kasa Kartı Anonimleştir'
                            wrapperClassName='col-12'
                          />
                          <CheckboxInput
                            name='trafficHgsCardAnonymized'
                            label='Trafik ve HGS Ceza Kartı Anonimleştir'
                            wrapperClassName='col-12'
                          />
                        </div>
                      </div>

                      <div className='col-md-6'>
                        <div className='row g-4'>
                          <CheckboxInput
                            name='carNotAllowed'
                            label='Araç Verilemez'
                            wrapperClassName='col-12'
                          />
                          <CheckboxInput
                            name='ageLicenseFree'
                            label='Yaş-Ehliyet Serbest'
                            wrapperClassName='col-12'
                          />
                          <CheckboxInput
                            name='showRenterOnInvoice'
                            label='Fat. Kiralayan Yaz'
                            wrapperClassName='col-12'
                          />
                          <CheckboxInput
                            name='branchSpecific'
                            label='Şubeye Özel'
                            wrapperClassName='col-12'
                          />
                          <CheckboxInput
                            name='hqCorporate'
                            label='Merkez Kurumsal'
                            wrapperClassName='col-12'
                          />
                          <CheckboxInput
                            name='brokerAgency'
                            label='Broker/Acenta'
                            wrapperClassName='col-12'
                          />
                          <CheckboxInput
                            name='findexRequired'
                            label='Findex Gerekli'
                            wrapperClassName='col-12'
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </Form>
              </div>
            </>
          )
        }}
      </Formik>
      <ToastContainer
        position='top-right'
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  )
}
